
import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal } from '@/common/ui/Core';
import { NotificationToast } from '@/common/ui/NotificationToast';
import { ViewContainerAction } from '@/common/detail-view/ViewContainer';

type NotificationType = 'success' | 'error' | 'info' | 'loading';


interface DetailViewState {
  isOpen: boolean;
  title: string;
  content: ReactNode;
  onBack?: () => void;
  onEdit?: boolean;
  actions?: ViewContainerAction[];
  footerActions?: ViewContainerAction[];
  viewType?: string; // Stable identifier for URL params (e.g., 'product-detail', 'new-payment')
  entityId?: string; // Optional ID for the entity being viewed/edited
  viewDescription?: string;
}

export interface CurtainAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

export interface CurtainState {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  description: string;
  children?: ReactNode;
  actions?: CurtainAction[];
}

interface UIContextType {
  openModal: (title: string, content: ReactNode, className?: string, hideHeader?: boolean, variant?: 'standard' | 'premium') => void;
  closeModal: () => void;
  showNotification: {
    (title: string, message: string, type?: NotificationType): void;
    (notification: NotificationToastProps): void;
  };
  openDetailView: (title: string, content: ReactNode, onBack?: () => void, onEdit?: boolean, actions?: ViewContainerAction[], footerActions?: ViewContainerAction[], viewType?: string, entityId?: string, viewDescription?: string) => void;
  closeDetailView: () => void;
  openCurtain: (config: Omit<CurtainState, 'isOpen'>) => void;
  closeCurtain: () => void;
  setGlobalDirty: (isDirty: boolean) => void;
  setShowUnsavedModal: (show: boolean) => void;
  onConfirmClose: (pending: boolean) => void;
  globalIsDirty: boolean;
  detailView: DetailViewState | null;
  curtain: CurtainState | null;
  showUnsavedModal: boolean;
  confirmClose: boolean;
  isGlobalExpanded: boolean;
  setIsGlobalExpanded: (expanded: boolean) => void;
}

interface ModalState {
  isOpen: boolean;
  title: string;
  content: ReactNode;
  className?: string;
  hideHeader?: boolean;
  variant?: 'standard' | 'premium';
}

interface NotificationState {
  id: number;
  title: string;
  message: string;
  type: NotificationType;
}
type NotificationToastProps = Omit<NotificationState, 'id'>

const noop = () => undefined;

const fallbackUIContext: UIContextType = {
  openModal: noop,
  closeModal: noop,
  showNotification: noop as UIContextType['showNotification'],
  openDetailView: noop,
  closeDetailView: noop,
  openCurtain: noop,
  closeCurtain: noop,
  setGlobalDirty: noop,
  setShowUnsavedModal: noop,
  onConfirmClose: noop,
  globalIsDirty: false,
  detailView: null,
  curtain: null,
  showUnsavedModal: false,
  confirmClose: false,
  isGlobalExpanded: false,
  setIsGlobalExpanded: noop,
};

const UIContext = createContext<UIContextType | undefined>(undefined);
let hasWarnedMissingUIProvider = false;

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modalStack, setModalStack] = useState<ModalState[]>([]);
  const [detailView, setDetailView] = useState<DetailViewState | null>(null);
  const [curtain, setCurtain] = useState<CurtainState | null>(null);
  const [globalIsDirty, setGlobalIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [confirmClose, onConfirmClose] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isGlobalExpanded, setIsGlobalExpanded] = useState(false);

  const [notifications, setNotifications] = useState<NotificationState[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const detailReturnSearchParamsRef = useRef<URLSearchParams | null>(null);
  const detailWasOpenRef = useRef(false);

  // URL Synchronization
  useEffect(() => {
    if (detailView?.isOpen && detailView.viewType) {
      detailWasOpenRef.current = true;
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('view', detailView.viewType!);
        next.delete('action');
        if (detailView.entityId) {
          next.set('id', detailView.entityId);
        } else {
          next.delete('id');
        }
        return next;
      }, { replace: true });
      setHasInitialized(true);
    } else if (hasInitialized && detailWasOpenRef.current) {
      detailWasOpenRef.current = false;
      // Only clear if we have already initialized (i.e., we are CLOSING an open view)
      if (detailReturnSearchParamsRef.current) {
        const returnSearchParams = new URLSearchParams(detailReturnSearchParamsRef.current);
        returnSearchParams.delete('action');
        detailReturnSearchParamsRef.current = null;
        setSearchParams(returnSearchParams, { replace: true });
      } else if (searchParams.has('view')) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete('view');
          next.delete('id');
          next.delete('action');
          return next;
        }, { replace: true });
      }
    }
  }, [detailView, searchParams, setSearchParams, hasInitialized]);

  const openModal = useCallback((title: string, content: ReactNode, className?: string, hideHeader?: boolean, variant?: 'standard' | 'premium') => {
    setModalStack((prev: ModalState[]) => {
      // Guard: Don't open the same modal twice if it's already on top
      if (prev.length > 0 && prev[prev.length - 1].title === title) {
        const newStack = [...prev];
        newStack[newStack.length - 1] = { isOpen: true, title, content, className, hideHeader, variant };
        return newStack;
      }
      return [...prev, { isOpen: true, title, content, className, hideHeader, variant }];
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalStack(prev => prev.slice(0, -1));
  }, []);

  const openDetailView = useCallback((title: string, content: ReactNode, onBack?: () => void, onEdit?: boolean, actions?: ViewContainerAction[], footerActions?: ViewContainerAction[], viewType?: string, entityId?: string, viewDescription?: string) => {
    const currentView = searchParams.get('view');
    detailReturnSearchParamsRef.current = currentView && currentView !== viewType
      ? new URLSearchParams(searchParams)
      : null;
    setDetailView({ isOpen: true, title, content, onBack, onEdit, actions, footerActions, viewType, entityId, viewDescription });
  }, [searchParams]);

  const closeDetailView = useCallback(() => {
    setDetailView(null);
    setGlobalIsDirty(false); // Clear dirty state when closing
  }, []);

  const openCurtain = useCallback((config: Omit<CurtainState, 'isOpen'>) => {
    setCurtain({ ...config, isOpen: true });
  }, []);

  const closeCurtain = useCallback(() => {
    setCurtain(prev => prev ? { ...prev, isOpen: false } : null);
    // Optional: delay clearing the state to allow exit animation
    setTimeout(() => {
      setCurtain(null);
    }, 700);
  }, []);

  const setGlobalDirty = (isDirty: boolean) => {
    setGlobalIsDirty(isDirty);
  };

  function showNotification(title: string, message: string, type?: NotificationType): void;
  function showNotification(notification: NotificationToastProps): void;
  function showNotification(arg1: string | NotificationToastProps, message?: string, type: NotificationType = 'success') {
    let newNotification: NotificationState;

    if (typeof arg1 === 'string') {
      // First overload style: individual arguments
      newNotification = {
        id: Date.now(),
        title: arg1,
        message: message!,
        type,
      };
    } else {
      // Second overload style: full object
      newNotification = {
        ...arg1,
      } as NotificationState & NotificationToastProps;
    }

    setNotifications((prev) => [...prev, newNotification]);
  };

  const removeNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const value = {
    openModal,
    closeModal,
    showNotification,
    openDetailView,
    closeDetailView,
    openCurtain,
    closeCurtain,
    setGlobalDirty,
    setShowUnsavedModal,
    onConfirmClose,
    globalIsDirty,
    showUnsavedModal,
    detailView,
    curtain,
    confirmClose,
    isGlobalExpanded,
    setIsGlobalExpanded,
  };
  const topModal = modalStack[modalStack.length - 1];

  return (
    <UIContext.Provider value={value}>
      {children}
      {topModal && (
        <Modal
          isOpen
          onClose={closeModal}
          title={topModal.title}
          className={topModal.className}
          hideHeader={topModal.hideHeader}
          variant={topModal.variant}
        >
          {topModal.content}
        </Modal>
      )}
      <div className="fixed top-5 right-5 z-[200] space-y-3">
        {notifications.map((n) => (
          <NotificationToast
            key={n.id}
            id={n.id}
            title={n.title}
            message={n.message}
            type={n.type}
            onClose={() => removeNotification(n.id)}
          />
        ))}
      </div>
    </UIContext.Provider>
  );
};

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);

  if (!context) {
    if (!hasWarnedMissingUIProvider) {
      console.error('useUI must be used within a UIProvider');
      hasWarnedMissingUIProvider = true;
    }
    return fallbackUIContext;
  }

  return context;
};
